/**
 * PermissionsMatrixPanel — UI per la gestione dinamica dei permessi RBAC.
 *
 * Mostra una matrice ruoli × permessi (raggruppati per modulo) con un
 * checkbox per ogni cella. Salva replace-all per ruolo (un PUT per ruolo).
 *
 * Solo OPS può aprire questa pagina (gate already a livello /settings).
 * I ruoli editabili sono area_manager, hr_manager, store_manager,
 * store_user (l'OPS è admin assoluto e non appare nella matrice).
 *
 * Introdotto 2026-05-29: RBAC dinamico — i flag attivi qui sostituiscono
 * gradualmente i check `if user.role in (...)` nel codice.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Save, ShieldCheck, AlertCircle, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';


const ROLE_LABELS = {
  area_manager: 'Area Manager',
  hr_manager: 'HR Manager',
  store_manager: 'Store Manager',
  store_user: 'Staff',
};


export function PermissionsMatrixPanel() {
  const [catalog, setCatalog] = useState(null);
  const [matrix, setMatrix] = useState({});       // {role: Set<key>}
  const [original, setOriginal] = useState({});   // baseline per dirty check
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});       // {role: bool}
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  // Fetch catalogo + matrice in parallelo
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [c, m] = await Promise.all([
          api.get('/permissions/catalog'),
          api.get('/permissions/matrix'),
        ]);
        if (!active) return;
        setCatalog(c.data);
        const asSet = {};
        const orig = {};
        for (const role of Object.keys(m.data.matrix)) {
          asSet[role] = new Set(m.data.matrix[role]);
          orig[role] = new Set(m.data.matrix[role]);
        }
        setMatrix(asSet);
        setOriginal(orig);
      } catch (e) {
        toast.error("Errore caricamento permessi");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const toggle = (role, key) => {
    setMatrix(prev => {
      const next = { ...prev, [role]: new Set(prev[role] || []) };
      if (next[role].has(key)) next[role].delete(key);
      else next[role].add(key);
      return next;
    });
  };

  const isDirty = (role) => {
    const a = matrix[role] || new Set();
    const b = original[role] || new Set();
    if (a.size !== b.size) return true;
    for (const k of a) if (!b.has(k)) return true;
    return false;
  };

  const totalDirty = useMemo(
    () => Object.keys(matrix).filter(isDirty).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matrix, original]
  );

  const saveRole = async (role) => {
    setSaving(prev => ({ ...prev, [role]: true }));
    try {
      const enabled_keys = Array.from(matrix[role] || []);
      await api.put('/permissions/matrix', { role, enabled_keys });
      setOriginal(prev => ({ ...prev, [role]: new Set(enabled_keys) }));
      toast.success(`${ROLE_LABELS[role]}: ${enabled_keys.length} permessi salvati`);
    } catch (e) {
      toast.error(`Errore: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(prev => ({ ...prev, [role]: false }));
    }
  };

  const resetRole = (role) => {
    setMatrix(prev => ({ ...prev, [role]: new Set(original[role] || []) }));
  };

  const toggleGroup = (groupKey) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  // Toggle "tutti i permessi di questo gruppo" per un ruolo (helper rapido)
  const toggleAllInGroup = (role, group) => {
    const allKeys = group.permissions.map(p => p.key);
    setMatrix(prev => {
      const current = new Set(prev[role] || []);
      const allOn = allKeys.every(k => current.has(k));
      const next = new Set(current);
      if (allOn) {
        allKeys.forEach(k => next.delete(k));
      } else {
        allKeys.forEach(k => next.add(k));
      }
      return { ...prev, [role]: next };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!catalog) return null;

  const roles = catalog.editable_roles;
  const totalPermissions = catalog.groups.reduce((s, g) => s + g.permissions.length, 0);

  return (
    <div className="space-y-6" data-testid="permissions-matrix-panel">
      {/* Header sintetico */}
      <div className="bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid="permissions-info">
        <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-900 leading-relaxed">
          <p className="font-semibold mb-1">RBAC dinamico (deny-by-default)</p>
          <p>
            Tutti i ruoli (tranne <span className="font-semibold">Operations Manager</span>, che resta admin assoluto)
            partono <span className="font-semibold">senza alcun permesso</span>. Attiva qui sotto solo le voci necessarie
            per ogni ruolo. Le modifiche vengono salvate per ruolo e sono effettive entro 60 secondi (cache).
            Catalogo: {totalPermissions} permessi su {catalog.groups.length} moduli.
          </p>
        </div>
      </div>

      {/* Toolbar: dirty counter + suggerimento */}
      {totalDirty > 0 && (
        <div className="bg-violet-50 border border-violet-200 px-4 py-2 text-xs text-violet-900 flex items-center gap-2" data-testid="dirty-banner">
          <ShieldCheck className="w-3.5 h-3.5" />
          {totalDirty} {totalDirty === 1 ? 'ruolo modificato' : 'ruoli modificati'} non salvati.
          Salva ogni ruolo separatamente con il pulsante a destra del nome ruolo.
        </div>
      )}

      {/* Matrice — header fisso con i ruoli, righe per ogni gruppo */}
      <div className="border border-slate-200 overflow-x-auto" data-testid="permissions-matrix-table">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
            <tr>
              <th className="text-left px-4 py-3 font-semibold uppercase text-[10px] tracking-wider text-slate-600 w-[40%]">
                Permesso
              </th>
              {roles.map(r => (
                <th key={r} className="px-3 py-3 text-center align-bottom">
                  <div className="font-semibold uppercase text-[10px] tracking-wider text-slate-700 mb-1">{ROLE_LABELS[r] || r}</div>
                  <div className="flex items-center justify-center gap-1.5">
                    {isDirty(r) && (
                      <button
                        type="button"
                        onClick={() => resetRole(r)}
                        className="text-[10px] text-slate-500 hover:text-slate-900"
                        title="Annulla modifiche per questo ruolo"
                        data-testid={`reset-${r}`}
                      >
                        <RotateCcw className="w-3 h-3" />
                      </button>
                    )}
                    <Button
                      onClick={() => saveRole(r)}
                      disabled={!isDirty(r) || saving[r]}
                      size="sm"
                      className="rounded-none h-7 text-[10px] px-2.5"
                      data-testid={`save-${r}`}
                    >
                      <Save className="w-3 h-3 mr-1" />
                      {saving[r] ? '...' : (isDirty(r) ? `Salva (${matrix[r]?.size || 0})` : `${matrix[r]?.size || 0}`)}
                    </Button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {catalog.groups.map((group) => {
              const collapsed = collapsedGroups.has(group.key);
              return (
                <React.Fragment key={group.key}>
                  {/* Group header row */}
                  <tr className="bg-slate-100 border-y border-slate-300">
                    <td
                      colSpan={1}
                      className="px-4 py-2 font-semibold uppercase text-[11px] tracking-wider text-slate-800 cursor-pointer hover:bg-slate-200"
                      onClick={() => toggleGroup(group.key)}
                      data-testid={`group-header-${group.key}`}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
                        {group.label}
                        <span className="text-slate-500 font-normal ml-1">({group.permissions.length})</span>
                      </span>
                    </td>
                    {/* Per ogni ruolo: checkbox "tutti del gruppo" */}
                    {roles.map(r => {
                      const allKeys = group.permissions.map(p => p.key);
                      const enabled = allKeys.filter(k => (matrix[r] || new Set()).has(k)).length;
                      const allOn = enabled === allKeys.length;
                      const someOn = enabled > 0 && !allOn;
                      return (
                        <td key={r} className="px-3 py-2 text-center align-middle">
                          <label className="inline-flex items-center gap-1 cursor-pointer text-[10px] text-slate-600 hover:text-slate-900">
                            <input
                              type="checkbox"
                              checked={allOn}
                              ref={el => { if (el) el.indeterminate = someOn; }}
                              onChange={() => toggleAllInGroup(r, group)}
                              className="w-3 h-3 accent-slate-900"
                              data-testid={`group-toggle-${group.key}-${r}`}
                            />
                            <span>{enabled}/{allKeys.length}</span>
                          </label>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Permission rows */}
                  {!collapsed && group.permissions.map(perm => (
                    <tr key={perm.key} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-2 pl-8">
                        <div className="text-sm font-medium text-slate-800">{perm.label}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{perm.description}</div>
                        <div className="text-[10px] text-slate-300 mt-0.5 font-mono">{perm.key}</div>
                      </td>
                      {roles.map(r => {
                        const on = (matrix[r] || new Set()).has(perm.key);
                        return (
                          <td key={r} className="px-3 py-2 text-center align-middle">
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggle(r, perm.key)}
                              className="w-4 h-4 accent-slate-900 cursor-pointer"
                              data-testid={`cell-${perm.key}-${r}`}
                              aria-label={`${perm.label} per ${ROLE_LABELS[r]}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-slate-400">
        I permessi sono effettivi entro 60 secondi (cache server). Le scritture qui sono tracciate nell'audit
        log (azione <code className="font-mono">permissions_matrix_update</code>).
      </p>
    </div>
  );
}
